import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal, MapPin } from "lucide-react";
import ListingCard from "../components/ListingCard";
import { publicApi } from "../lib/api";
import { useContent, renderHighlightedTitle } from "../lib/useContent";
import { PageSEO } from "../components/PageSEO";

const PAGE_SIZE = 12;

// -- Sub components ---------------------------------------------------------
const FilterBar = ({ q, setQ, cat, setCat, sort, setSort, categories }) => (
  <section className="py-6 sticky top-[80px] z-30 bg-white/85 backdrop-blur-xl border-y border-[#E5E7EB]">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-stretch md:items-center gap-3">
      <div className="flex-1 flex items-center gap-3 bg-[#F8F9FA] rounded-2xl px-4 py-2 border border-[#E5E7EB]">
        <Search className="w-4 h-4 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per nome, servizio, prodotto…" className="w-full bg-transparent focus:outline-none py-2" />
      </div>
      <div className="flex items-center gap-2 overflow-x-auto md:overflow-visible">
        <CategoryPill active={cat === "tutte"} label="Tutte" onClick={() => setCat("tutte")} />
        {categories.map((c) => (
          <CategoryPill key={c.slug} active={cat === c.slug} label={c.name} onClick={() => setCat(c.slug)} />
        ))}
      </div>
      <div className="flex items-center gap-2">
        <SlidersHorizontal className="w-4 h-4 text-slate-500" />
        <select value={sort} onChange={(e) => setSort(e.target.value)} className="bg-white border border-[#E5E7EB] rounded-full px-4 py-2 text-sm focus:outline-none">
          <option value="name">A – Z</option>
          <option value="newest">Ultimi aggiornati</option>
        </select>
      </div>
    </div>
  </section>
);

const CategoryPill = ({ active, label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-4 py-2 rounded-full text-sm whitespace-nowrap border transition-colors ${
      active
        ? "bg-[#1F2937] text-white border-[#1F2937]"
        : "bg-white text-slate-800 border-[#E5E7EB] hover:border-slate-400"
    }`}
  >
    {label}
  </button>
);

const ResultsSkeleton = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    {Array.from({ length: 6 }).map((_, i) => (
      <div key={`sk-${i}`} className="bg-white rounded-3xl overflow-hidden border border-[#F1F5F9]">
        <div className="aspect-[16/10] skeleton" />
        <div className="p-6 space-y-3">
          <div className="h-4 skeleton rounded w-24" />
          <div className="h-6 skeleton rounded w-3/4" />
          <div className="h-4 skeleton rounded w-full" />
        </div>
      </div>
    ))}
  </div>
);

const ResultsError = ({ message }) => (
  <div className="py-24 text-center">
    <p className="font-display text-3xl text-[#1F2937]">Directory in attivazione</p>
    <p className="text-slate-500 mt-3 max-w-lg mx-auto">{message}</p>
    <p className="text-slate-400 mt-6 text-sm">Configurazione database in corso. Riprova tra qualche minuto.</p>
  </div>
);

const ResultsEmpty = () => (
  <div className="py-24 text-center">
    <p className="font-display text-3xl text-[#1F2937]">Nessuna attività trovata.</p>
    <p className="text-slate-500 mt-3">Prova a modificare i filtri o la parola chiave.</p>
  </div>
);

const ResultsGrid = ({ rows }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    {rows.map((l) => <ListingCard key={l.id || l.slug} listing={l} />)}
  </div>
);

const ResultsSection = ({ loading, error, rows }) => {
  if (loading) return <ResultsSkeleton />;
  if (error) return <ResultsError message={error} />;
  if (rows.length === 0) return <ResultsEmpty />;
  return <ResultsGrid rows={rows} />;
};

// -- Main component ---------------------------------------------------------
const Explore = () => {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [cat, setCat] = useState(params.get("cat") || "tutte");
  const [sort, setSort] = useState("name");
  const [page] = useState(1);

  const [categories, setCategories] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load categories once
  useEffect(() => {
    let cancelled = false;
    publicApi.listCategories()
      .then((r) => { if (!cancelled) setCategories(r.data || []); })
      .catch((err) => { console.warn("Categories load failed", err); });
    return () => { cancelled = true; };
  }, []);

  // Sync URL params with filters
  useEffect(() => {
    const next = new URLSearchParams();
    if (q) next.set("q", q);
    if (cat && cat !== "tutte") next.set("cat", cat);
    setParams(next, { replace: true });
  }, [q, cat, setParams]);

  // Reset filter helper that also resets pagination
  const applyFilter = useCallback((updater) => {
    updater();
    // pagination reset would go here when we add real paging
  }, []);

  const setQuery = useCallback((v) => applyFilter(() => setQ(v)), [applyFilter]);
  const setCategory = useCallback((v) => applyFilter(() => setCat(v)), [applyFilter]);

  // Fetch results
  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    const query = {
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
      order: sort,
    };
    if (q) query.q = q;
    if (cat && cat !== "tutte") query.category = cat;
    publicApi.listBusinesses(query)
      .then((r) => { if (!cancelled) setRows(r.data || []); })
      .catch((err) => {
        if (cancelled) return;
        const detail = err?.response?.data?.detail || "Impossibile caricare le attività.";
        setError(detail);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [q, cat, sort, page]);

  const { field, seo } = useContent("businesses");
  const heroPretitle = field("hero", "pretitle", "Attività");
  const heroHeadline = field("hero", "headline", "Trova negozi, bar e servizi a Sanluri.");
  const heroHighlight = field("hero", "highlight_terms", ["a Sanluri"]);
  const countTemplate = field("labels", "count_template", "{n} attività trovate");
  const locationLabel = field("labels", "location_label", "Sanluri (SU), Sardegna");
  const countLabel = useMemo(() => countTemplate.replace("{n}", String(rows.length)), [rows.length, countTemplate]);

  return (
    <div className="pt-32">
      <PageSEO seo={seo} defaults={{ title: "Attività — CCN Sanluri", description: "Trova negozi, bar, artigiani e servizi del centro di Sanluri." }} />
      <section className="pb-8">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          {heroPretitle !== "" && <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-6">{heroPretitle}</div>}
          <h1 className="font-display font-bold text-5xl md:text-7xl leading-[1.02] tracking-tight text-[#1F2937] max-w-4xl">
            {renderHighlightedTitle(heroHeadline, heroHighlight)}
          </h1>
        </div>
      </section>

      <FilterBar q={q} setQ={setQuery} cat={cat} setCat={setCategory} sort={sort} setSort={setSort} categories={categories} />

      <section className="py-14">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-center justify-between mb-8">
            <div className="text-slate-600">{countLabel}</div>
            <div className="hidden md:flex items-center gap-2 text-sm text-slate-500">
              <MapPin className="w-4 h-4" /> {locationLabel}
            </div>
          </div>
          <ResultsSection loading={loading} error={error} rows={rows} />
        </div>
      </section>
    </div>
  );
};

export default Explore;
